import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import dynamic from "next/dynamic";
import getConfig from "next/config";
import { useMap } from "@vis.gl/react-google-maps";
import { debounce } from "lodash";

import GoogleMapFilters from "@components/GoogleMap/GoogleMapFilters";
import {
  LockerInfoWindowContent,
  LockerInfoWindowHeader,
} from "@components/Lockers/LockerInfoWindow";
import LockerMarker from "@components/Lockers/LockerMarker";
import LockerList from "@components/Lockers/LockerList";
import Loading from "@components/Loading";
import FavouriteLockerView from "@components/Lockers/FavouriteLockerView";
import { addAlert, apiCall } from "@store/app/actions";
import { getRegions, setFavouriteLockerId } from "@store/user/actions";
import SelectedLockerView from "@components/Lockers/SelectedLockerView";
import getAvailableMarkers from "@components/GoogleMap/_lib/getAvailableMarkers";
import useVisibleMarkers from "@components/GoogleMap/useVisibleMarkers";
import useMarkersClusterer from "@components/GoogleMap/useMarkersClusterer";
import LockersClustererIcon from "@components/Lockers/LockersClustererIcon";
import { LOCKER_INACTIVE_MESSAGE } from "@components/Lockers/_lib/constants";
import isLockerInactive from "@components/Lockers/_lib/isLockerInactive";
import { getLockersList } from "@store/stores/actions";
import { localClientStore } from "@lib/clientStore";

const GoogleMap = dynamic(() => import("@components/GoogleMap"), {
  ssr: false,
});

const {
  publicRuntimeConfig: {
    google: { googleMapsCloudId },
  },
} = getConfig();

const LockersMapWithFilters = memo(
  ({
    user,
    addAlert,
    mapId,
    getLockersList,
    getRegions,
    setFavouriteLockerId,
    lockers: initialData,
    regions,
    onLockerSelect,
  }) => {
    const map = useMap(mapId);
    const { customer, isLoggedIn } = user;
    const customerFavoriteLockerId =
      customer?.locker_id || localClientStore.get("favourite_locker_id");

    const filteredLockersRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
      const getData = async () => {
        setIsLoading(true);
        const promises = [];

        if (!initialData?.length) {
          promises.push(getLockersList());
        }

        if (!regions?.length) {
          promises.push(getRegions());
        }

        if (promises.length) {
          await Promise.all(promises);
        }
        setIsLoading(false);
      };

      if (!initialData?.length || !regions?.length) {
        getData();
      }
    }, []);

    const {
      visibleMarkers,
      changeInitialVisibleMarkers,
      handleVisibleMarkersUpdate,
    } = useVisibleMarkers({
      initialData,
      map,
    });

    // selectedMarker should contain both the marker instance and the marker data
    const { selectedMarker, onMarkerSelect, onMarkerDeselect } =
      useMarkersClusterer({
        map,
        data: visibleMarkers,
        mapMarkerIcon: LockerMarker,
        clustererIcon: LockersClustererIcon,
      });

    const customerFavoriteLocker = useMemo(
      () =>
        customerFavoriteLockerId && initialData?.length
          ? initialData.find((locker) => locker.id === customerFavoriteLockerId)
          : null,
      [customerFavoriteLockerId, initialData]
    );

    const handleFiltersChange = ({ value, active }) => {
      if (!filteredLockersRef.current) {
        filteredLockersRef.current = {};
      }

      switch (active) {
        case "region": {
          // when user selects a region, filter regions from all lockers list
          if (value && value !== "all_regions") {
            const filteredLockers = getAvailableMarkers({
              data: initialData,
              activeInput: active,
              filter: value,
            });

            filteredLockersRef.current = { region: filteredLockers };
            handleVisibleMarkersUpdate(filteredLockers);
          }
          // when user selects "all regions", we reset map to default position
          // also search and city filters are reset
          else if (value === "all_regions") {
            filteredLockersRef.current = null;

            handleVisibleMarkersUpdate();
          }
          break;
        }

        case "city": {
          // when user selects a city, we filter based on the previously filtered list by region
          // if no region is seleced, city field is disabled
          if (value && value !== "all_cities") {
            const filteredLockers = getAvailableMarkers({
              data: filteredLockersRef.current.region,
              activeInput: active,
              filter: value,
            });

            filteredLockersRef.current = {
              ...filteredLockersRef.current,
              city: filteredLockers,
            };

            handleVisibleMarkersUpdate(filteredLockers);
          }
          // if user selects "all cities", we reset to the previously filtered list by region
          else if (value === "all_cities") {
            filteredLockersRef.current = {
              region: filteredLockersRef.current.region,
            };
            handleVisibleMarkersUpdate(filteredLockersRef.current.region);
          }
          break;
        }

        case "search": {
          if (value && value.trim().length > 2) {
            // when user enters a search term, we search based on the previously filtered list
            // if we have city, search in city, otherwise if we have region, search in region list,
            // otherwise we search based on all lockers list

            debounce(() => {
              const visibleLockers = getAvailableMarkers({
                data: filteredLockersRef.current?.city
                  ? filteredLockersRef.current.city
                  : filteredLockersRef.current?.region
                  ? filteredLockersRef.current.region
                  : initialData,
                activeInput: active,
                filter: value,
              });

              if (visibleLockers?.length) {
                filteredLockersRef.current = {
                  ...filteredLockersRef.current,
                  search: visibleLockers,
                };

                handleVisibleMarkersUpdate(visibleLockers);
              }
            }, 500)();
          }
          // if value is empty string, we know that a search was previously made
          else if (value === "") {
            // if we have region we could have city as well
            // if search is cleared, we want to reset to the previous list based on region/city filters
            if (filteredLockersRef.current?.region) {
              filteredLockersRef.current = {
                region: filteredLockersRef.current.region,
                ...(filteredLockersRef.current.city && {
                  city: filteredLockersRef.current.city,
                }),
              };

              handleVisibleMarkersUpdate(
                filteredLockersRef.current?.city
                  ? filteredLockersRef.current.city
                  : filteredLockersRef.current.region
              );
            } else {
              // if no region or city filters, we reset to all lockers list and fit map to default position
              filteredLockersRef.current = null;
              handleVisibleMarkersUpdate();
            }
          }
          break;
        }
        default:
          break;
      }
    };

    const handleSaveOrDeleteFavouriteLocker = async ({ action }) => {
      // Deleting stays available — it is the way out of an inactive favourite
      if (action !== "delete" && isLockerInactive(selectedMarker?.marker)) {
        return addAlert({
          message: LOCKER_INACTIVE_MESSAGE,
          variant: "warning",
        });
      }

      setIsLoading(true);

      if (isLoggedIn) {
        const payload = action === "delete" ? null : selectedMarker.marker.id;

        const response = await setFavouriteLockerId(payload);

        if (response?.status === "success") {
          if (selectedMarker) {
            onMarkerDeselect();
          }

          addAlert({
            message:
              response.messages?.[0] ||
              (action === "delete"
                ? "Punctul favorit a fost șters."
                : "Punctul a fost salvat ca favorit."),
            variant: "success",
          });
        } else {
          addAlert({
            message:
              action === "delete"
                ? "Nu am putut sterge punctul favorit."
                : "Nu am putut salva punctul favorit.",
            variant: "error",
          });
        }
      } else {
        if (action === "add") {
          setFavouriteLockerId(selectedMarker.marker.id);
        }

        if (action === "delete") {
          setFavouriteLockerId(null);
        }

        if (selectedMarker) {
          onMarkerDeselect();
        }
      }

      setIsLoading(false);
    };

    const handleGeolocationChange = useCallback(() => {
      changeInitialVisibleMarkers();
    }, [changeInitialVisibleMarkers]);

    const handleLockerSelect = useCallback(() => {
      // The disabled buttons are the UI, this guard is the contract
      if (isLockerInactive(selectedMarker?.marker)) {
        return addAlert({
          message: LOCKER_INACTIVE_MESSAGE,
          variant: "warning",
        });
      }

      onLockerSelect(selectedMarker.marker);
    }, [addAlert, onLockerSelect, selectedMarker]);

    if (!isLoading && !initialData?.length) {
      return (
        <div className="flex items-center justify-center w-full h-[300px] border border-gray-1100 mt-[32px]">
          <p className="text-center text-gray-1200">
            Nu am putut prelua lista cu punctele de livrare. Te rugam sa incerci
            din nou mai tarziu.
          </p>
        </div>
      );
    }

    return (
      <div className="flex flex-col lg:flex-row-reverse w-full h-full">
        <div className="w-full h-[60vh] md:h-[80vh]">
          {initialData.length > 0 && (
            <GoogleMap
              mapId={mapId}
              mapCloudId={googleMapsCloudId}
              infoWindow={{
                content: LockerInfoWindowContent,
                header: LockerInfoWindowHeader,
              }}
              onMarkerDeselect={onMarkerDeselect}
              selectedMarker={selectedMarker}
              onGeolocationChange={handleGeolocationChange}
            />
          )}
        </div>

        {/* Sidebar with filters and locker list - LEFT on desktop */}
        {initialData.length > 0 && (
          <div className="flex flex-col pr-[16px] w-full h-[60vh] lg:h-[80vh] lg:w-[40%] xl:w-[30%] bg-white">
            <GoogleMapFilters
              data={filteredLockersRef.current?.region || initialData}
              onFiltersChange={handleFiltersChange}
            />

            {customerFavoriteLocker && !selectedMarker && (
              <div className="my-[12px] ">
                <FavouriteLockerView
                  locker={customerFavoriteLocker}
                  onSaveOrDeleteFavouriteLocker={() =>
                    handleSaveOrDeleteFavouriteLocker({ action: "delete" })
                  }
                />
              </div>
            )}

            {selectedMarker ? (
              <div className="mt-[20px]">
                <SelectedLockerView
                  locker={selectedMarker.marker}
                  onSaveOrDeleteFavouriteLocker={() =>
                    handleSaveOrDeleteFavouriteLocker({ action: "add" })
                  }
                  onLockerDeselect={onMarkerDeselect}
                  onLockerSelect={onLockerSelect ? handleLockerSelect : null}
                />
              </div>
            ) : (
              <LockerList
                data={visibleMarkers}
                onLockerSelect={onMarkerSelect}
              />
            )}
          </div>
        )}

        {isLoading && (
          <div className="absolute flex items-center justify-center inset-0 bg-white opacity-60">
            <Loading height={100} />
          </div>
        )}
      </div>
    );
  }
);

LockersMapWithFilters.displayName = "LockersMapWithFilters";

export default connect(
  (state) => ({
    user: state.user,
    lockers: state.stores.lockers,
    regions: state.user.regions,
  }),
  {
    apiCall,
    addAlert,
    getLockersList,
    getRegions,
    setFavouriteLockerId,
  }
)(LockersMapWithFilters);
